import { Injectable } from '@angular/core';
import { Lookup } from '@common/utilities/Lookup';
import { DataService, ProductRecord } from './data.service';
import { SynchronizationService } from './synchronization.service';
import { ApiService } from './api.service';

interface SKU {
  SKU: string;
  Products: ProductRecord[];
}

interface SKUColor {
  SKU: string;
  Color: string;
  Product: ProductRecord;
}

@Injectable({
  providedIn: 'root'
})
export class ProductsService {

  private lookupBySKU = new Lookup<SKU>(i => i.SKU);
  private lookupBySKUColor = new Lookup<SKUColor>(i => [i.SKU, i.Color].join('/'));

  private _sync(notify = true) {

    if (notify) this.sync.notify(ProductsService, false);

    this.ds.products('list').subscribe(products => {

      if (products) {
        this.lookupBySKU.source = products.group(i => i.SKU).map(i => ({ SKU: i.key as string, Products: i.items }));
        this.lookupBySKUColor.source = products.many(i => i.Colors.map(c => ({ SKU: i.SKU, Color: c.Color, Product: i })));
        this.sync.notify(ProductsService, true);
      }
      else {
        if (this.api.authorized) {
          this._sync(false);
        }
        else {
          this.api.credentialChanged.subscribe(user => {
            this._sync(false);
          });
        }
      }
    });
  }

  public synchronize() {
    this._sync();
  }

  private clone(product: ProductRecord): ProductRecord {
    return product ? { ...product, Colors: product.Colors.map(i => ({ ...i })) } : null;
  }

  public get(sku: string, color: string = undefined): ProductRecord[] {

    if (color) {

      let key = [sku, color].join('/');

      return this.lookupBySKUColor.has(key) ? [this.clone(this.lookupBySKUColor.get(key).Product)] : [];
    }
    else {
      return this.lookupBySKU.has(sku) ? this.lookupBySKU.get(sku).Products.map(i => this.clone(i)) : [];
    }
  }

  public get all(): ProductRecord[] {
    return this.lookupBySKU.source.many(i => i.Products).map(i => this.clone(i));
  }

  constructor(private ds: DataService, private api: ApiService, private sync: SynchronizationService) {
    this._sync();
  }
}
